import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { HdInfoChipVm, HdInfoSectionVm, HdInfoSectionsTarget, HdInfoSectionsVm } from './hd-info-sections.models';

/**
 * Generiek skelet van de boektekst-info-panelen (Basics, Type, Strategie,
 * Signatuur, Autoriteit, Definitie): titel met springknop, algemeen document,
 * highlight-strook met chips en de knop "Toon alle …", en secties met
 * feitenregel en één document of delen. Domein-onafhankelijk: de
 * paneelcomponenten leveren een {@link HdInfoSectionsVm}.
 *
 * Eigen paneel: enkel de eigen sectie zichtbaar (zonder eigen sectie: alles);
 * "Toon alle" wordt aan de paneelcomponent gemeld, die een apart
 * overzichtspaneel opent. Overzichtsmodus (`collapsible`): alle secties als
 * inklapbare lijst, kop en feiten zichtbaar, inhoud pas na openklikken.
 * Chips scrollen naar een sectie of deel (en klappen die eerst open).
 */
@Component({
  selector: 'hd-info-sections',
  standalone: true,
  imports: [MarkdownComponent],
  templateUrl: './hd-info-sections.component.html',
  styleUrl: './hd-info-sections.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdInfoSectionsComponent implements AfterViewChecked {
  readonly vm = input.required<HdInfoSectionsVm>();

  /** Klik op "Toon alle …": de paneelcomponent opent het overzichtspaneel. */
  readonly showAllRequested = output<void>();

  /** Klik op de knop naast de titel (`headerActionLabel`). */
  readonly headerActionRequested = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly hasOwn = computed(() => this.vm().sections.some(section => section.isOwn));

  protected readonly hasShowAll = computed(() =>
    !this.vm().collapsible && this.hasOwn() && !!this.vm().strip?.showAllLabel);

  protected readonly visibleSections = computed<readonly HdInfoSectionVm[]>(() => {
    const sections = this.vm().sections;
    if (this.vm().collapsible || !this.hasOwn()) {
      return sections;
    }
    return sections.filter(section => section.isOwn);
  });

  /**
   * Startstaat van de uitklapping. Eigen `equal`: enkel een echte wijziging
   * (andere `expansionKey` of andere open secties) zet de staat terug, niet
   * elk nieuw vm-object (bv. een taal-tick).
   */
  private readonly expansionSeed = computed(
    () => ({ key: this.vm().expansionKey ?? '', open: this.vm().initiallyExpanded ?? [] }),
    { equal: (a, b) => a.key === b.key && a.open.join('|') === b.open.join('|') },
  );

  /** Opengeklapte secties in de overzichtsmodus. */
  private readonly expanded = linkedSignal<{ key: string; open: readonly string[] }, ReadonlySet<string>>({
    source: this.expansionSeed,
    computation: seed => new Set(seed.open),
  });

  /** Doel van de laatste chip-klik; wordt na de volgende view-check gescrold (de sectie kan net zichtbaar geworden zijn). */
  private pendingTarget: HdInfoSectionsTarget | null = null;

  ngAfterViewChecked(): void {
    const target = this.pendingTarget;
    if (!target) {
      return;
    }
    this.pendingTarget = null;
    const section = `[data-section-key="${target.sectionKey}"]`;
    const selector = target.partKey ? `${section} [data-part-key="${target.partKey}"]` : section;
    this.host.nativeElement.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected isExpanded(key: string): boolean {
    return !this.vm().collapsible || this.expanded().has(key);
  }

  protected toggleSection(key: string): void {
    this.expanded.update(expanded => {
      const next = new Set(expanded);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  protected requestHeaderAction(): void {
    this.headerActionRequested.emit();
  }

  protected requestShowAll(): void {
    this.showAllRequested.emit();
  }

  /** Chip: klap de sectie open wanneer nodig, dan scrollen. */
  protected onChipClick(chip: HdInfoChipVm): void {
    if (this.vm().collapsible && !this.expanded().has(chip.sectionKey)) {
      this.toggleSection(chip.sectionKey);
    }
    this.pendingTarget = chip;
  }

  protected scrollToStrip(): void {
    this.host.nativeElement.querySelector('.hd-info-sections__strip')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
